<section class="document-view-page">
  <header class="page-head">
    <button type="button" class="back-link" (click)="navigateBackToList()">← Back to documents</button>
  </header>

  @if (loading) {
    <p class="state-banner">Loading document overview…</p>
  } @else if (errorMessage) {
    <div class="state-card">
      <p class="error-banner">{{ errorMessage }}</p>
      <button type="button" class="ghost-btn" (click)="retry()">Retry</button>
    </div>
  } @else if (document) {
    <section class="overview-shell">
      <header class="overview-card">
        <div class="hero-row">
          <div class="hero-copy">
            <h1>{{ document.document.title }}</h1>
            <p class="hero-subtitle">
              {{ document.sourceType === 'template' ? (document.sourceTemplateName || 'Template-based document') : 'Uploaded document' }}
            </p>
          </div>

          <div class="hero-actions">
            @if (primaryAction; as action) {
              <button
                type="button"
                class="primary-btn"
                [disabled]="!action.available || isBusy(action.id)"
                (click)="handleAction($event, action)"
              >
                {{ action.label }}
              </button>
            }

            @if (menuActions.length > 0) {
              <div class="menu-shell">
                <button
                  type="button"
                  class="ghost-btn menu-trigger"
                  aria-haspopup="menu"
                  [attr.aria-expanded]="menuOpen"
                  (click)="toggleMenu($event)"
                >
                  <span>More actions</span>
                  <svg class="caret-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>

                @if (menuOpen) {
                  <div class="actions-menu" role="menu" aria-label="Document actions">
                    @for (action of menuActions; track action.id) {
                      <button
                        type="button"
                        class="menu-item"
                        role="menuitem"
                        [class.is-destructive]="action.destructive"
                        [disabled]="!action.available || isBusy(action.id)"
                        (click)="handleAction($event, action)"
                      >
                        {{ action.label }}
                      </button>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </div>

        @if (successMessage) {
          <p class="status-banner success">{{ successMessage }}</p>
        }

        <dl class="summary-grid">
          @for (item of summaryItems; track item.label) {
            <div class="summary-row" [class.is-wide]="item.multiline">
              <dt>{{ item.label }}</dt>
              <dd [class.multiline]="item.multiline">{{ item.value }}</dd>
            </div>
          }
        </dl>
      </header>

      <section class="detail-card">
        <div class="tab-list" role="tablist" aria-label="Document details tabs">
          <button
            type="button"
            class="tab-btn"
            role="tab"
            [attr.aria-selected]="activeTab === 'recipients'"
            [class.is-active]="activeTab === 'recipients'"
            (click)="selectTab('recipients')"
          >
            Recipient Details
          </button>
          <button
            type="button"
            class="tab-btn"
            role="tab"
            [attr.aria-selected]="activeTab === 'history'"
            [class.is-active]="activeTab === 'history'"
            (click)="selectTab('history')"
          >
            Document History
          </button>
        </div>

        @if (activeTab === 'recipients') {
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Recipient Details</th>
                  <th>Delivery Mode</th>
                  <th>Last Activity</th>
                  <th>Status</th>
                  <th>Authentication</th>
                </tr>
              </thead>
              <tbody>
                @if (recipientRows.length === 0) {
                  <tr>
                    <td colspan="5" class="empty-row">No recipients found.</td>
                  </tr>
                } @else {
                  @for (row of recipientRows; track row.id) {
                    <tr>
                      <td>
                        <div class="recipient-cell">
                          <div class="recipient-avatar" aria-hidden="true">
                            {{ (row.name || row.email || 'R').charAt(0).toUpperCase() }}
                          </div>
                          <div class="recipient-meta">
                            <p class="person-name">{{ row.name }}</p>
                            <p class="person-email">{{ row.email }}</p>
                          </div>
                        </div>
                      </td>
                      <td><span class="delivery-badge">{{ row.deliveryMode }}</span></td>
                      <td class="activity-cell">{{ row.lastActivity }}</td>
                      <td>
                        <span class="recipient-status" [ngClass]="row.statusClass">{{ row.statusLabel }}</span>
                      </td>
                      <td><span class="auth-tag">{{ row.authentication }}</span></td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date and Time</th>
                  <th>User Activity</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                @if (historyRows.length === 0) {
                  <tr>
                    <td colspan="3" class="empty-row">No document history yet.</td>
                  </tr>
                } @else {
                  @for (row of historyRows; track row.at + row.action) {
                    <tr>
                      <td>{{ row.at }}</td>
                      <td>{{ row.message }}</td>
                      <td>{{ row.action }}</td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        }
      </section>
    </section>
  }

  @if (document; as documentToDelete) {
    @if (deleteConfirmOpen) {
    <div class="delete-modal-mask" role="presentation" (click)="closeDeleteConfirm()">
      <section
        class="delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-document-title"
        (click)="$event.stopPropagation()"
      >
        <div class="delete-modal-body">
          <h3 id="delete-document-title">Delete document?</h3>
          <p>
            Delete <strong>{{ documentToDelete.document.title }}</strong>? This removes it from your documents list.
          </p>
        </div>
        <footer class="delete-modal-actions">
          <button type="button" class="ghost-btn" (click)="closeDeleteConfirm()" [disabled]="isBusy('delete')">
            Cancel
          </button>
          <button type="button" class="danger-btn" (click)="confirmDelete($event)" [disabled]="isBusy('delete')">
            Delete
          </button>
        </footer>
      </section>
    </div>
    }
  }
</section>
